import React, { useCallback, useEffect, useRef, useState } from "react";
import { Maximize, Pause, Play, Volume2, VolumeX } from "lucide-react";
import "./CardVideoPlayer.css";

function formatTime(seconds) {
  const time = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  return `${Math.floor(time / 60)}:${String(time % 60).padStart(2, "0")}`;
}

function PlaybackButton({ onActivate, preferPointerEvents = false, children, ...props }) {
  const touchStart = useRef(null);
  const pointerStart = useRef(null);
  const ignoreClickUntil = useRef(0);
  const supportsPointerEvents = preferPointerEvents && typeof window.PointerEvent === "function";

  return (
    <button
      {...props}
      type="button"
      onPointerDown={(event) => {
        if (!supportsPointerEvents || event.pointerType === "mouse" || event.isPrimary === false) return;
        pointerStart.current = { id: event.pointerId, x: event.clientX, y: event.clientY };
      }}
      onPointerMove={(event) => {
        const start = pointerStart.current;
        if (start && (event.pointerId !== start.id || Math.hypot(event.clientX - start.x, event.clientY - start.y) > 10)) {
          pointerStart.current = null;
        }
      }}
      onPointerCancel={() => { pointerStart.current = null; }}
      onPointerUp={(event) => {
        const start = pointerStart.current;
        pointerStart.current = null;
        if (props.disabled || !start || start.id !== event.pointerId) return;
        event.preventDefault();
        event.stopPropagation();
        ignoreClickUntil.current = Date.now() + 700;
        onActivate();
      }}
      onTouchStart={(event) => {
        const touch = event.touches.length === 1 ? event.touches[0] : null;
        touchStart.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
      }}
      onTouchMove={(event) => {
        const touch = event.touches[0];
        const start = touchStart.current;
        if (!touch || event.touches.length !== 1 || (start && Math.hypot(touch.clientX - start.x, touch.clientY - start.y) > 10)) {
          touchStart.current = null;
        }
      }}
      onTouchCancel={() => { touchStart.current = null; }}
      onTouchEnd={(event) => {
        if (supportsPointerEvents || props.disabled || !touchStart.current || event.touches.length) return;
        touchStart.current = null;
        // Play in touchend itself; cancel Safari's delayed synthetic click.
        event.preventDefault();
        event.stopPropagation();
        ignoreClickUntil.current = Date.now() + 700;
        onActivate();
      }}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (props.disabled || (event.detail !== 0 && Date.now() < ignoreClickUntil.current)) return;
        onActivate();
      }}
    >
      {children}
    </button>
  );
}

export default function CardVideoPlayer({
  src, poster, title, className = "", mediaClassName = "", mediaContent,
  onVideoElement, onPlayRequest, onVideoPlay, onVideoPause, onVideoSeek, onVideoTimeUpdate,
}) {
  const videoRef = useRef(null);
  const attachVideo = useCallback((element) => {
    videoRef.current = element;
    onVideoElement?.(element);
  }, [onVideoElement]);
  const playRequest = useRef(0);
  const [isIOS] = useState(() => /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));
  const [hasStarted, setHasStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [failedPoster, setFailedPoster] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hasMetadata, setHasMetadata] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isNativeFullscreen, setIsNativeFullscreen] = useState(false);
  const customControls = isIOS && !isNativeFullscreen;
  // Prepare an iOS frame fallback even if the saved poster later fails to decode.
  // Callers key by src; poster updates must never reload an active video.
  const [videoSource] = useState(() => isIOS && !src.includes("#") ? `${src}#t=0.001` : src);
  const activePoster = poster && poster !== failedPoster ? poster : undefined;

  useEffect(() => {
    setFailedPoster(null);
    if (!poster) return undefined;
    let cancelled = false;
    const image = new Image();
    // The video's error event does not report broken or empty poster images.
    image.onerror = () => { if (!cancelled) setFailedPoster(poster); };
    image.src = poster;
    return () => {
      cancelled = true;
      image.onerror = null;
    };
  }, [poster]);

  let originalVideoHref;
  try {
    const url = new URL(src, window.location.href);
    if (url.protocol === "http:" || url.protocol === "https:") originalVideoHref = url.href;
  } catch {
    // Invalid URLs have no external fallback link.
  }

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const enterFullscreen = () => setIsNativeFullscreen(true);
    const leaveFullscreen = () => setIsNativeFullscreen(false);
    const fullscreenChanged = () => setIsNativeFullscreen(document.fullscreenElement === video);
    video.addEventListener("webkitbeginfullscreen", enterFullscreen);
    video.addEventListener("webkitendfullscreen", leaveFullscreen);
    document.addEventListener("fullscreenchange", fullscreenChanged);
    return () => {
      video.removeEventListener("webkitbeginfullscreen", enterFullscreen);
      video.removeEventListener("webkitendfullscreen", leaveFullscreen);
      document.removeEventListener("fullscreenchange", fullscreenChanged);
    };
  }, [videoError]);

  const startPlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    const request = ++playRequest.current;
    const wasStarted = hasStarted;
    setHasStarted(true);
    setIsPlaying(true);
    const failed = (error) => {
      if (videoRef.current !== video || playRequest.current !== request) return;
      setIsPlaying(false);
      setHasStarted(wasStarted);
      onVideoPause?.(video);
      if (error?.name !== "AbortError" && error?.name !== "NotAllowedError") setVideoError(true);
    };
    try {
      // Keep playback inside the user's gesture, without waiting for media events.
      onPlayRequest?.(video);
      video.play()?.catch(failed);
    } catch (error) {
      failed(error);
    }
  };

  const togglePlayback = () => {
    if (isPlaying) {
      ++playRequest.current;
      videoRef.current?.pause();
      setIsPlaying(false);
    } else {
      startPlayback();
    }
  };

  const readDuration = (event) => {
    const value = event.currentTarget.duration;
    setDuration(Number.isFinite(value) && value > 0 ? value : 0);
  };

  const enterFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;
    const failed = () => {
      if (videoRef.current !== video) return;
      video.controls = false;
      setIsNativeFullscreen(false);
    };
    try {
      video.controls = true;
      setIsNativeFullscreen(true);
      if (typeof video.webkitEnterFullscreen === "function" && video.webkitSupportsFullscreen !== false) {
        video.webkitEnterFullscreen();
      } else {
        video.requestFullscreen()?.catch(failed);
      }
    } catch {
      failed();
    }
  };

  const supportsFullscreen = (typeof videoRef.current?.webkitEnterFullscreen === "function"
    && videoRef.current?.webkitSupportsFullscreen !== false)
    || typeof videoRef.current?.requestFullscreen === "function";

  return (
    <div
      className={`card-video-player ${customControls ? "card-video-player--custom" : ""}`}
      role="group"
      aria-label={`Video player: ${title || "Video"}`}
      onClick={(event) => event.stopPropagation()}
    >
      {videoError ? (
        <div className="card-video-player__error">
          <p>This video couldn’t load in your browser.</p>
          {originalVideoHref && <a href={originalVideoHref} target="_blank" rel="noopener noreferrer">Open original video</a>}
        </div>
      ) : (
        <>
          <div className={`card-video-player__media ${mediaClassName}`}>
            <video
              ref={attachVideo}
              aria-label={title || "Video"}
              className={`card-video-player__video ${className} ${mediaContent && !isNativeFullscreen ? "card-video-player__video--composited" : ""}`}
              src={videoSource}
              poster={activePoster}
              preload="auto"
              playsInline
              controls={isNativeFullscreen || (!isIOS && hasStarted)}
              onLoadedMetadata={(event) => { setHasMetadata(true); readDuration(event); }}
              onDurationChange={readDuration}
              onTimeUpdate={(event) => {
                setCurrentTime(event.currentTarget.currentTime);
                onVideoTimeUpdate?.(event.currentTarget);
              }}
              onVolumeChange={(event) => setIsMuted(event.currentTarget.muted)}
              onPlay={(event) => {
                const video = event.currentTarget;
                // A queued play event can arrive after another preview paused this one.
                if (video.paused) return;
                setHasStarted(true);
                setIsPlaying(true);
                onVideoPlay?.(video);
              }}
              onPause={(event) => {
                const video = event.currentTarget;
                if (!video.paused) return;
                setIsPlaying(false);
                onVideoPause?.(video);
              }}
              onEnded={(event) => { setIsPlaying(false); onVideoPause?.(event.currentTarget); }}
              onSeeking={(event) => onVideoSeek?.(event.currentTarget)}
              onSeeked={(event) => onVideoSeek?.(event.currentTarget)}
              onError={(event) => { onVideoPause?.(event.currentTarget); setVideoError(true); }}
            />
            {mediaContent && <div className="card-video-player__composition" hidden={isNativeFullscreen}>{mediaContent}</div>}
            {/* Keep the iOS tap target mounted so its compatibility click stays suppressed. */}
            {(!hasStarted || customControls) && (
              <PlaybackButton
                preferPointerEvents={isIOS}
                className="card-video-player__start"
                aria-label={`${!hasStarted ? "Play" : isPlaying ? "Pause" : "Resume"} ${title || "video"}`}
                onActivate={togglePlayback}
              >
                {!isPlaying && <span><Play aria-hidden="true" /></span>}
              </PlaybackButton>
            )}
          </div>
          {customControls && hasStarted && (
            <div className="card-video-player__controls" role="group" aria-label="Video controls">
              <PlaybackButton preferPointerEvents={isIOS} aria-label={isPlaying ? "Pause video" : "Play video"} onActivate={togglePlayback}>
                {isPlaying ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
              </PlaybackButton>
              <span className="card-video-player__time">{formatTime(currentTime)} / {formatTime(duration)}</span>
              <input
                aria-label="Seek video"
                type="range"
                min="0"
                max={duration || 0}
                step="0.1"
                value={Math.min(currentTime, duration || 0)}
                disabled={!duration}
                onClick={(event) => event.stopPropagation()}
                onChange={(event) => {
                  const time = Number(event.target.value);
                  const video = videoRef.current;
                  if (!video || !duration) return;
                  video.currentTime = time;
                  setCurrentTime(time);
                  onVideoSeek?.(video);
                }}
              />
              <PlaybackButton preferPointerEvents={isIOS} aria-label={isMuted ? "Unmute video" : "Mute video"} onActivate={() => {
                const video = videoRef.current;
                if (!video) return;
                video.muted = !video.muted;
                setIsMuted(video.muted);
              }}>
                {isMuted ? <VolumeX aria-hidden="true" /> : <Volume2 aria-hidden="true" />}
              </PlaybackButton>
              {supportsFullscreen && (
                <PlaybackButton preferPointerEvents={isIOS} aria-label="Full screen" disabled={!hasMetadata} onActivate={enterFullscreen}>
                  <Maximize aria-hidden="true" />
                </PlaybackButton>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
